// Axial hex math — kept in lockstep with sdk-typescript/src/hex.ts,
// server/mecharena/hex.py, and apps/web/src/lib/hex.ts.
// Flat-top grid, facing N=0 NE=1 SE=2 S=3 SW=4 NW=5.

export interface Hex {
  q: number;
  r: number;
}

export const DIRECTIONS: ReadonlyArray<readonly [number, number]> = [
  [0, -1],
  [1, -1],
  [1, 0],
  [0, 1],
  [-1, 1],
  [-1, 0],
];

export function hexDistance(a: Hex, b: Hex): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return Math.floor((Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2);
}

export function hexesInRadius(radius: number): Hex[] {
  const out: Hex[] = [];
  for (let q = -radius; q <= radius; q++) {
    const rLo = Math.max(-radius, -q - radius);
    const rHi = Math.min(radius, -q + radius);
    for (let r = rLo; r <= rHi; r++) {
      out.push({ q, r });
    }
  }
  return out;
}

// Pixel layout for flat-top hex. `size` is the radius of the
// circumscribed circle (corner-to-center distance).
export function hexToPixel(h: Hex, size: number): { x: number; y: number } {
  const x = size * ((3 / 2) * h.q);
  const y = size * ((Math.sqrt(3) / 2) * h.q + Math.sqrt(3) * h.r);
  return { x, y };
}

export function hexCorners(h: Hex, size: number): Array<{ x: number; y: number }> {
  const { x: cx, y: cy } = hexToPixel(h, size);
  const corners: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i);
    corners.push({
      x: cx + size * Math.cos(angle),
      y: cy + size * Math.sin(angle),
    });
  }
  return corners;
}
